import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fullName, updateProfileSchema } from "@tc/shared";
import { Avatar, Button, Card, Field, Icon, TextInput, Toast } from "@/components/ui";
import { useAuthz } from "@/lib/auth/authz";
import { initials } from "@/app/layouts/parts";
import {
  ProfileSaveError,
  removeProfilePhoto,
  updateProfile,
  uploadProfilePhoto,
} from "../api";

const MAX_PHOTO_BYTES = 2 * 1024 * 1024; // 2 MB — matches the design's "JPG or PNG, up to 2 MB".
const ACCEPTED_TYPES = ["image/jpeg", "image/png"];

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  phone: string;
}

/**
 * Split a stored full name into first/last for editing — the first token is the first name, the rest
 * is the last name. Recombining via `fullName()` preserves the original content (only the conceptual
 * boundary is heuristic). The single `user.name` column is unchanged; the split is presentation-only,
 * mirroring how the invite forms collect first/last and combine server-side.
 */
function splitName(full: string): { firstName: string; lastName: string } {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  return { firstName: parts[0] ?? "", lastName: parts.slice(1).join(" ") };
}

/**
 * Settings → Profile (TW-3464). Self-service editing of the caller's own identity: name, job
 * title, email and phone, plus an avatar with live preview. Club and role are structural and shown
 * read-only. The save bar only enables once the form is dirty and flags unsaved changes; cancel
 * reverts. On success it re-pulls /api/me (authz.refresh) so the topbar + user-card update live.
 */
export function ProfileSection() {
  const { t } = useTranslation();
  const { me, refresh } = useAuthz();

  const initial = useMemo<FormState>(
    () => ({
      ...splitName(me?.name ?? ""),
      email: me?.email ?? "",
      jobTitle: me?.jobTitle ?? "",
      phone: me?.phone ?? "",
    }),
    [me],
  );

  const [form, setForm] = useState<FormState>(initial);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Re-seed the form when /api/me arrives or changes (e.g. after a refresh) and we are not mid-edit.
  const [seededFrom, setSeededFrom] = useState(initial);
  if (seededFrom !== initial && !dirty(form, seededFrom)) {
    setSeededFrom(initial);
    setForm(initial);
  }

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 2600);
  }

  const isDirty = dirty(form, initial);

  function set<K extends keyof FormState>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  }

  async function save() {
    // Both name parts are required; combine them into the single stored `name` (mirrors the invite
    // forms, which collect first/last and store one `user.name`).
    const next: Partial<Record<keyof FormState, string>> = {};
    if (!form.firstName.trim()) next.firstName = t("settings.profile.required");
    if (!form.lastName.trim()) next.lastName = t("settings.profile.required");

    const payload = {
      name: fullName(form.firstName, form.lastName),
      email: form.email,
      jobTitle: form.jobTitle,
      phone: form.phone,
    };
    const parsed = updateProfileSchema.safeParse(payload);
    if (!parsed.success) {
      for (const issue of parsed.error.issues) {
        // The schema validates the combined `name`; surface that error under the first-name field.
        const field = (issue.path[0] === "name" ? "firstName" : issue.path[0]) as keyof FormState;
        if (field && !next[field]) next[field] = issue.message;
      }
    }
    if (Object.keys(next).length > 0 || !parsed.success) {
      setErrors(next);
      return;
    }
    setSaving(true);
    try {
      await updateProfile(parsed.data);
      await refresh();
      flash(t("settings.profile.saved"));
    } catch (err) {
      if (err instanceof ProfileSaveError && err.status === 409) {
        setErrors({ email: t("settings.profile.emailTaken") });
      } else {
        flash(t("settings.profile.saveError"));
      }
    } finally {
      setSaving(false);
    }
  }

  function cancel() {
    setForm(initial);
    setErrors({});
  }

  return (
    <div>
      <div className="set-section-head" style={{ marginBottom: 18 }}>
        <h2>{t("settings.profile.heading")}</h2>
        <div className="sub">{t("settings.profile.sub")}</div>
      </div>

      <Card>
        <PhotoRow
          imageUrl={me?.image ?? null}
          name={fullName(form.firstName, form.lastName) || me?.name || ""}
          onChange={refresh}
          flash={flash}
        />

        <div className="form-grid" style={{ marginTop: 22 }}>
          <Field label={t("settings.profile.firstName")} required>
            <TextInput icon="user" value={form.firstName} onChange={(v) => set("firstName", v)} />
            <FieldError msg={errors.firstName} />
          </Field>
          <Field label={t("settings.profile.lastName")} required>
            <TextInput icon="user" value={form.lastName} onChange={(v) => set("lastName", v)} />
            <FieldError msg={errors.lastName} />
          </Field>
          <Field label={t("settings.profile.jobTitle")}>
            <TextInput value={form.jobTitle} onChange={(v) => set("jobTitle", v)} placeholder={t("settings.profile.jobTitlePlaceholder")} />
          </Field>
          <Field label={t("settings.profile.email")} required>
            <TextInput icon="mail" type="email" value={form.email} onChange={(v) => set("email", v)} />
            <FieldError msg={errors.email} />
          </Field>
          <Field label={t("settings.profile.phone")}>
            <TextInput value={form.phone} onChange={(v) => set("phone", v)} placeholder={t("settings.profile.phonePlaceholder")} />
          </Field>
        </div>

        {/* Roles are hidden in the club portal — only back-office users see their role here. */}
        <ReadOnlyOrg
          clubName={me?.clubName ?? null}
          role={me?.userType === "BACK_OFFICE" ? (me?.roles[0] ?? null) : null}
        />

        <div className="card-foot">
          {isDirty && (
            <span className="foot-note">
              <span className="badge-dot" style={{ background: "var(--warning)" }} />
              {t("settings.profile.unsaved")}
            </span>
          )}
          <Button variant="ghost" onClick={cancel} disabled={!isDirty || saving}>
            {t("actions.cancel")}
          </Button>
          <Button variant="primary" icon="check" onClick={save} disabled={!isDirty || saving}>
            {t("settings.profile.save")}
          </Button>
        </div>
      </Card>

      {toast && <Toast message={toast} />}
    </div>
  );
}

/** Avatar with live preview + change/remove. Uploads go through the signed-URL flow in api.ts. */
function PhotoRow({
  imageUrl,
  name,
  onChange,
  flash,
}: {
  imageUrl: string | null;
  name: string;
  onChange: () => Promise<void>;
  flash: (msg: string) => void;
}) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  async function pick(file: File | undefined) {
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) return flash(t("settings.profile.photoType"));
    if (file.size > MAX_PHOTO_BYTES) return flash(t("settings.profile.photoSize"));
    setBusy(true);
    try {
      await uploadProfilePhoto(file);
      await onChange();
      flash(t("settings.profile.photoUpdated"));
    } catch {
      flash(t("settings.profile.photoError"));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await removeProfilePhoto();
      await onChange();
      flash(t("settings.profile.photoRemoved"));
    } catch {
      flash(t("settings.profile.photoError"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="photo-row">
      <Avatar initials={initials(name)} src={imageUrl} size={88} className="photo-lg" />
      <div className="photo-actions">
        <div className="pa-btns">
          <Button variant="secondary" size="sm" icon="upload" disabled={busy} onClick={() => fileRef.current?.click()}>
            {t("settings.profile.uploadPhoto")}
          </Button>
          {imageUrl && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={remove}>
              {t("settings.profile.removePhoto")}
            </Button>
          )}
        </div>
        <span className="faint" style={{ fontSize: 12 }}>
          {t("settings.profile.photoHint")}
        </span>
      </div>
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED_TYPES.join(",")}
        style={{ display: "none" }}
        onChange={(e) => void pick(e.target.files?.[0])}
      />
    </div>
  );
}

/** Read-only club + role, with the "contact your administrator" note. */
function ReadOnlyOrg({ clubName, role }: { clubName: string | null; role: string | null }) {
  const { t } = useTranslation();
  if (!clubName && !role) return null;
  return (
    <div className="profile-org">
      {clubName && (
        <div className="set-row">
          <div className="sr-text">
            <div className="sr-label">{t("settings.profile.club")}</div>
          </div>
          <span className="muted">{clubName}</span>
        </div>
      )}
      {role && (
        <div className="set-row">
          <div className="sr-text">
            <div className="sr-label">{t("settings.profile.role")}</div>
            <div className="sr-sub">{t("settings.profile.orgNote")}</div>
          </div>
          <span className="muted">{role}</span>
        </div>
      )}
    </div>
  );
}

function FieldError({ msg }: { msg?: string }) {
  if (!msg) return null;
  return (
    <div className="field-err">
      <Icon name="alert" size={13} /> {msg}
    </div>
  );
}

/** Shallow compare two form snapshots (after trimming) to drive the dirty/unsaved state. */
function dirty(a: FormState, b: FormState): boolean {
  return (
    a.firstName.trim() !== b.firstName.trim() ||
    a.lastName.trim() !== b.lastName.trim() ||
    a.email.trim() !== b.email.trim() ||
    a.jobTitle.trim() !== b.jobTitle.trim() ||
    a.phone.trim() !== b.phone.trim()
  );
}
